import { useMemo } from 'react';
import { useAuthStore } from '../../store/authStore';
import { useProfessorTurmas } from '../../api/hooks/useProfessorTurmas';
import {
  useMeusAlunosProfessor,
  type AlunoProfessorPick,
} from '../../api/hooks/useMeusAlunosProfessor';

export const TURNO_LABEL_ALUNOS: Record<string, string> = {
  MANHA: 'Manhã',
  TARDE: 'Tarde',
  NOITE: 'Noite',
  INTEGRAL: 'Integral',
};

export type AlunoListaProfessor = AlunoProfessorPick & {
  turmaNome: string;
  turmaTurno?: string;
};

export function useMeusAlunosProfessorPage() {
  const user = useAuthStore((s) => s.user);
  const { turmaIds, turmas, isLoading: loadingGrade } = useProfessorTurmas(user?.id);
  const { alunos: raw, isLoading: loadingAlunos } = useMeusAlunosProfessor(turmaIds);

  const turmaMeta = useMemo(() => {
    const m = new Map<string, { nome: string; turno?: string }>();
    turmas.forEach((t) => m.set(t.id, { nome: t.nome, turno: t.turno }));
    return m;
  }, [turmas]);

  const alunos = useMemo((): AlunoListaProfessor[] => {
    return raw.map((a) => ({
      ...a,
      turmaNome: a.turma?.nome ?? turmaMeta.get(a.turmaId || '')?.nome ?? 'Sem turma',
      turmaTurno: turmaMeta.get(a.turmaId || '')?.turno,
    }));
  }, [raw, turmaMeta]);

  const turmasOpcoes = useMemo(
    () =>
      turmas
        .filter((t) => alunos.some((a) => a.turmaId === t.id))
        .map((t) => ({ id: t.id, nome: t.nome, turno: t.turno }))
        .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')),
    [turmas, alunos],
  );

  const turnosDisponiveis = useMemo(() => {
    const set = new Set<string>();
    turmasOpcoes.forEach((t) => {
      if (t.turno) set.add(t.turno);
    });
    return Array.from(set).sort();
  }, [turmasOpcoes]);

  return {
    alunos,
    turmasOpcoes,
    turnosDisponiveis,
    isLoading: loadingGrade || loadingAlunos,
    totalTurmasGrade: turmas.length,
  };
}
